/**
 * Item Categories Master — admin CRUD.
 * sub_type drives the UI on the Item form:
 *   RAW_COTTON → shows cotton fields
 *   SPARES     → shows catalog/drawing/part_no/equipment_ref
 *   CHEMICAL/PACKING → pack_unit/size/concentration
 */
import { useState } from 'react'
import {
  Button, Form, Input, Select, Tag, Space, App, Modal, Table, Switch,
} from 'antd'
import { PlusOutlined, EditOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { itemCategoriesApi } from '@/features/categories/api'
import type { ItemCategory, ItemSubType } from '@/shared/types/categories'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const SUB_TYPES: { value: ItemSubType; label: string; color: string }[] = [
  { value: 'RAW_COTTON', label: 'Raw Cotton',     color: 'green' },
  { value: 'SPARES',     label: 'Spares',         color: 'volcano' },
  { value: 'CONSUMABLE', label: 'Consumable',     color: 'orange' },
  { value: 'PACKING',    label: 'Packing',        color: 'gold' },
  { value: 'CHEMICAL',   label: 'Chemical',       color: 'red' },
  { value: 'YARN',       label: 'Yarn (FG)',      color: 'purple' },
  { value: 'OTHER',      label: 'Other',          color: 'default' },
]

export default function ItemCategoriesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<ItemCategory | null>(null)

  const canCreate = usePermission('item.create')
  const canUpdate = usePermission('item.update')
  const canManage = canCreate || canUpdate

  const { data = [], isLoading } = useQuery({
    queryKey: ['item-categories-all'],
    queryFn: () => itemCategoriesApi.list(false),
  })

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editing
        ? itemCategoriesApi.update(editing.id, values as Partial<ItemCategory>)
        : itemCategoriesApi.create(values as { code: string; name: string; sub_type?: ItemSubType }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-categories-all'] })
      qc.invalidateQueries({ queryKey: ['item-categories'] })
      message.success(editing ? 'Category updated' : 'Category created')
      setOpen(false)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      itemCategoriesApi.update(id, { is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-categories-all'] })
      message.success('Updated')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ sub_type: 'OTHER' })
    setOpen(true)
  }

  const openEdit = (r: ItemCategory) => {
    setEditing(r)
    form.setFieldsValue(r)
    setOpen(true)
  }

  const columns = [
    { title: 'Code', dataIndex: 'code', width: 220, render: (v: string) => <Tag style={{ fontFamily: 'monospace' }}>{v}</Tag> },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Sub-type', dataIndex: 'sub_type',
      render: (v?: string) => {
        if (!v) return '—'
        const st = SUB_TYPES.find(s => s.value === v)
        return <Tag color={st?.color ?? 'default'}>{st?.label ?? v}</Tag>
      },
    },
    {
      title: 'Type', dataIndex: 'is_system',
      render: (v: boolean) => v ? <Tag color="blue">System</Tag> : <Tag>Custom</Tag>,
    },
    {
      title: 'Status', dataIndex: 'is_active',
      render: (v: boolean) => v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, r: ItemCategory) => (
        <Space>
          {canManage && <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>}
          {canManage && (
            <Button
              size="small"
              icon={r.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
              danger={r.is_active}
              onClick={() => toggleMutation.mutate({ id: r.id, is_active: !r.is_active })}
              disabled={r.is_system && r.is_active}
            >
              {r.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Item Categories"
        subtitle="Sub-type drives the fields shown on the Item form (e.g. Spares → Catalog, Drawing, Part No.)"
        extra={canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Category</Button>
        )}
      />
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={isLoading}
        size="middle"
        pagination={false}
      />

      <Modal
        title={editing ? 'Edit Category' : 'New Category'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.validateFields().then(v => saveMutation.mutate(v))}
        confirmLoading={saveMutation.isPending}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editing && (
            <Form.Item name="code" label="Code" rules={[{ required: true, pattern: /^[A-Z0-9_]+$/, message: 'Uppercase + underscores only' }]}>
              <Input placeholder="e.g. SPARES_PNEUMATIC" style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} />
            </Form.Item>
          )}
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Spares - Pneumatic" />
          </Form.Item>
          <Form.Item name="sub_type" label="Sub-Type" rules={[{ required: true }]}
            help="Controls which fields appear on the Item form for this category">
            <Select options={SUB_TYPES.map(s => ({ value: s.value, label: s.label }))} />
          </Form.Item>
          {editing && (
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  )
}
